import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountService } from 'app/account/services/account.service';
import { debounceTime, firstValueFrom, from, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { DataLockService } from '../core/data-lock/data-lock.service';
import Query from '../core/query/query';
import { DialogService } from '../shared/dialog/dialog.service';
import { SearchResultService } from './search-result.service';
import { SearchService } from './search.service';

@Component({
	selector: 'ca-search',
	templateUrl: './search.component.html',
	styleUrls: ['./search.component.scss'],
	standalone: false
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('searchInput') searchInput: any;
	@ViewChild('auto') autocomplete: any;

	public searchResults: any[] = [];
	public searchFormControl = new FormControl();
	public query = new Query();
	public searchBoxWidth = '0';
	public searchFormControlFocus = false;
	public isInFocus = false;
	private allCommands: any[] = [];
	public searchValue: any = '';
	public resultConfiguration: Record<string, any> = {};
	public offset = 0;
	public inputValue = '';
	public skipSearch = false;

	private searchResultsType: string = 'flat';
	private destroy$ = new Subject<void>();

	constructor(
		public searchService: SearchService,
		public dialogService: DialogService,
		public dataLockService: DataLockService,
		private accountService: AccountService,
		public searchResultService: SearchResultService,
		public router: Router
	) {
		this.resultConfiguration = this.searchResultService.getResultFormats();
	}

	public groupBy(array: any[] = [], prop: string) {
		const groupsMap = new Map<string, any[]>();

		for (const item of array) {
			const key = item?.[prop];
			if (!groupsMap.has(key)) groupsMap.set(key, []);
			groupsMap.get(key)!.push(item);
		}

		const groups: any[] = [];
		groupsMap.forEach((items, name) => groups.push({ name, items }));
		return groups;
	}

	public ngAfterViewInit() {
		// complete selection on TAB
		try {
			this.autocomplete?._keyManager?.tabOut?.pipe(takeUntil(this.destroy$)).subscribe(() => {
				const selection = (this.autocomplete._keyManager.activeItem || {}).value;
				if (selection && selection.type === 'command') {
					selection.value = selection.value.trim() + ' ';
					this.resultSelect(selection);
					this.searchValue = selection.value;
				}
			});
		} catch (e) {
			// safe-guard for components without autocomplete ready
		}
	}

	public ngOnInit() {
		this.offset = 0;

		this.searchFormControl.valueChanges.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe((inputValue) => {
			if (inputValue) {
				this.inputValue = inputValue;
				this.search(inputValue, this.offset, false);
			}
		});
	}

	public onSearchKeydown(event: KeyboardEvent) {
		// prevent losing focus from search input if user press tab/enter after entering single word command
		const wordsCount = String(this.searchValue || '')
			.trim()
			.split(' ').length;
		if ((event.code === 'Tab' || event.code === 'Enter') && wordsCount === 1) {
			this.searchResults = [];
			return false;
		}
		this.offset = 0;
	}

	public async resultSelect(result: any) {
		const type = result.type;
		const group = result.group;
		const config = this.resultConfiguration[group];

		if (config) {
			if (config.action === 'navigate') {
				const navigateConfig = config.action_config;
				const navPromise =
					result.group === 'account'
						? firstValueFrom(this.accountService.getByGpxId(result.id).pipe(switchMap((account) => from(this.router.navigate([navigateConfig.path, account.id, navigateConfig.view])))))
						: this.router.navigate([navigateConfig.path, result.id, navigateConfig.view]);

				await navPromise;
			} else if (config.action === 'open') {
				this.openModal(result.id, config);
			}

			this.closeForm();
		}

		if (type !== 'command') {
			this.searchFormControl.setValue('');
			this.closeForm();
		} else {
			const val = result.value.endsWith(':') ? [result.value] : [result.value, ''];
			this.searchValue = val.join('');
			this.searchFormControl.setValue(this.searchValue);
		}

		this.searchResults = [];
	}

	public openModal(id: any, config: any) {
		const actionConfig = config.action_config;
		const service = actionConfig.service;
		const entityName = actionConfig.openParams;
		const componentType = actionConfig.componentType;

		if (!id) return;

		if (config.path) {
			this.router.navigate([config.path]);
		}

		const openDialog = (detailItem: any) => {
			const openParams: any = { [entityName]: detailItem, viewOnly: true };
			const width = config.path === '/contact' ? '1150px' : '800px';
			this.dialogService.open(componentType, openParams, { width });
		};

		if (config.path === '/user') {
			service.search(id).pipe(takeUntil(this.destroy$)).subscribe(openDialog);
		} else {
			service.findById(id).pipe(takeUntil(this.destroy$)).subscribe(openDialog);
		}
	}

	public search(q: any, offset: number, loadMore: boolean) {
		const searchValue = q?.value ?? q ?? '';

		if (searchValue && searchValue.entity) return;

		if (!loadMore) {
			this.searchResults = [];
			this.offset = 0;
		}

		this.searchService
			.search(q, offset)
			.pipe(takeUntil(this.destroy$))
			.subscribe((result: any) => {
				const searchResults = this.normalizeSearchResults(result);

				const commandsResults = searchResults.filter((r) => r.type === 'command');
				const otherResults = searchResults.filter((r) => r.type !== 'command');

				if (!loadMore) {
					this.searchResults = [...commandsResults, ...otherResults];
				} else {
					this.searchResults.push(...commandsResults, ...otherResults);
				}

				if (this.searchResults.length && searchResults[0]?.type) {
					this.searchResults = this.searchResults.filter((item) => item.value !== String(q).trim());
				}

				setTimeout(() => {
					try {
						this.autocomplete._keyManager.setFirstItemActive();
					} catch (e) {
						// ignore if not available
					}
				}, 250);
			});
	}

	private normalizeSearchResults(result: any): any[] {
		if (!result) return [];

		if (!result.items) {
			return (result || []).map((item: any) => ({ ...item, group: 'Qualifiers' }));
		}

		if (result.type === 'group') {
			return result.items.map((item: any) => ({ ...item, group: item.group }));
		}

		return result.items.map((item: any) => ({ ...item, group: item.group }));
	}

	onScrollHandler($event: any) {
		const element = $event.target || $event.srcElement;
		if (Math.ceil(element.scrollTop) >= element.scrollHeight - element.clientHeight - 50 && this.searchResults[0] && this.searchResults[0].total > this.searchResults.length) {
			this.offset += 100;
			this.search(this.inputValue, this.offset, true);
		}
	}

	public closeForm() {
		setTimeout(() => {
			if (this.searchValue && this.searchValue.type === 'command') {
				this.searchResults = [];
				this.offset = 0;
				return;
			}

			this.closeSearchBox();
			this.searchResults = [];
			this.offset = 0;
		}, 250);
	}

	public openForm() {
		setTimeout(() => {
			this.openSearchBox();
		}, 250);
	}

	public toggleSearch() {
		if (this.searchBoxWidth === '0') {
			this.openSearchBox();
		} else {
			this.closeSearchBox();
		}
	}

	private closeSearchBox() {
		this.searchResults = [];
		this.offset = 0;
		this.searchBoxWidth = '0';
		this.searchFormControlFocus = false;
		this.isInFocus = false;
		this.searchValue = '';
	}

	private openSearchBox() {
		this.searchBoxWidth = '400px';
		this.searchFormControlFocus = true;
		this.isInFocus = true;
		this.searchValue = '';
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
